import { useEffect } from "react";

import {
  FROM_JOB_SEARCH_KEY,
  JOB_SEARCH_KEY,
  PROCESS_SEARCH_KEY,
} from "@/config/query-parameter-consts";
import { useQuerySearchState } from "@/hooks/use-query-search-state";
import { useGetJobQuery } from "@/queries/query-job";
import { useGetProcessQuery } from "@/queries/query-process";

const useProcessAndJobSearch = () => {
  const getJobQuery = useGetJobQuery();
  const {
    data: job,
    isLoading: isLoadingJob,
    setData: setJob,
  } = useQuerySearchState(JOB_SEARCH_KEY, getJobQuery, {
    clearOthers: [FROM_JOB_SEARCH_KEY],
  });

  // The sourceJob is used to copy the inputs and outputs of a job
  // to the form when starting a new process run based on an existing job
  const { data: sourceJob, setData: setSourceJob } = useQuerySearchState(
    FROM_JOB_SEARCH_KEY,
    getJobQuery,
    {
      clearOthers: [JOB_SEARCH_KEY],
    }
  );

  const getProcessQuery = useGetProcessQuery();
  const {
    data: process,
    isLoading: isLoadingProcess,
    setData: setProcess,
    setDataId: setProcessId,
  } = useQuerySearchState(
    PROCESS_SEARCH_KEY,
    // If the search state id is undefined use the job process id
    // so the process id doesnt need to be in the url for the process to be defined same for the sourceJob
    (processId) => getProcessQuery(processId ?? job?.process?.id ?? sourceJob?.process?.id),
    {
      clearOthers: [JOB_SEARCH_KEY, FROM_JOB_SEARCH_KEY],
    }
  );

  // Remove process id as soon as a job has a process id set.
  // Don't do it in clearOthers because the job id being set doesn't always mean the process id in the job is set.
  // and we don't want to remove the process id before the process id is set in the job because that would cause the process to be undefined for a moment
  const someJobHasProcessId: boolean = !!job?.process?.id || !!sourceJob?.process?.id;
  useEffect(() => {
    if (someJobHasProcessId) setProcessId(undefined, { dontSaveInHistory: true, clearOthers: [] });
  }, [someJobHasProcessId, setProcessId]);

  const hasJobOrProcess = process || isLoadingProcess || job || isLoadingJob;

  return {
    job,
    setJob,
    process,
    setProcess,
    setProcessId,
    hasJobOrProcess,
    sourceJob,
    setSourceJob,
    changeCurrentJobToSourceJob: () => {
      if (!job) {
        console.warn("Tried to copy job to run from while one of them is not defined");
        return;
      }
      setSourceJob(job);
    },
  };
};

export function useProcessOverview() {
  const {
    job,
    setJob,
    process,
    setProcess,
    setProcessId,
    hasJobOrProcess,
    changeCurrentJobToSourceJob,
    setSourceJob,
    sourceJob,
  } = useProcessAndJobSearch();

  return {
    process,
    hasJobOrProcess,
    setProcess,
    setProcessId,
    job,
    setJob,
    setSourceJob,
    changeCurrentJobToSourceJob,
    sourceJob,
  };
}

export type UseProcessOverviewReturn = ReturnType<typeof useProcessOverview>;
